import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import router from '@/router';

/**
 * Account Switcher Store Module
 * 
 * Enables multi-account switching for users with "+" email addresses
 * (e.g., tareef+acme@happily.ai, tareef+bigcorp@happily.ai)
 * 
 * Similar to Slack's workspace switcher, this allows partners and
 * Customer Success reps to quickly switch between customer accounts.
 */

export interface AccountInfo {
  email: string;           // Full email with + (e.g., tareef+acme@happily.ai)
  company: string;         // Company key
  companyName: string;     // Display name (e.g., "Acme Corp")
  initials: string;        // Two-letter initials for avatar
  color: string;           // Background color for avatar
  lastAccess: number;      // Timestamp of last access
  isSSO: boolean;          // Whether this account uses SSO (requires re-auth)
}

export interface AccountSwitcherState {
  baseEmail: string;              // Base email without + (e.g., tareef@happily.ai)
  accounts: AccountInfo[];        // All cached accounts
  activeEmail: string;            // Currently active account email
  isMultiAccountUser: boolean;    // Whether user has + email pattern
  isSwitching: boolean;           // Loading state during switch
  switchingToCompany: string;     // Company name being switched to (for overlay)
}

// Color palette for account avatars (excluding similar colors)
const AVATAR_COLORS = [
  '#6C30CC', // Purple (primary brand)
  '#32C2D5', // Cyan (secondary brand)
  '#E91E63', // Pink
  '#FF5722', // Deep Orange
  '#4CAF50', // Green
  '#2196F3', // Blue
  '#FFC107', // Amber
  '#795548', // Brown
  '#607D8B', // Blue Grey
  '#9C27B0', // Purple variant
];

// Local storage key for account switcher data
const STORAGE_KEY = 'accountSwitcher';

// Per-account localStorage key for cached user data (kept separate for performance)
function getAccountDataKey(email: string): string {
  return 'accountData_' + email.toLowerCase();
}

export default {
  namespaced: true,
  
  state: (): AccountSwitcherState => ({
    baseEmail: '',
    accounts: [],
    activeEmail: '',
    isMultiAccountUser: false,
    isSwitching: false,
    switchingToCompany: '',
  }),

  mutations: {
    setBaseEmail(state: AccountSwitcherState, email: string) {
      state.baseEmail = email;
    },
    
    setAccounts(state: AccountSwitcherState, accounts: AccountInfo[]) {
      state.accounts = accounts;
    },
    
    setActiveEmail(state: AccountSwitcherState, email: string) {
      state.activeEmail = email;
    },
    
    setIsMultiAccountUser(state: AccountSwitcherState, value: boolean) {
      state.isMultiAccountUser = value;
    },
    
    setIsSwitching(state: AccountSwitcherState, value: boolean) {
      state.isSwitching = value;
    },
    
    setSwitchingToCompany(state: AccountSwitcherState, value: string) {
      state.switchingToCompany = value;
    },
    
    addAccount(state: AccountSwitcherState, account: AccountInfo) {
      // Remove existing entry for this email if present
      state.accounts = state.accounts.filter(a => a.email !== account.email);
      // Add new/updated account
      state.accounts.push(account);
    },
    
    removeAccount(state: AccountSwitcherState, email: string) {
      state.accounts = state.accounts.filter(a => a.email !== email);
    },
    
    updateLastAccess(state: AccountSwitcherState, email: string) {
      const account = state.accounts.find(a => a.email === email);
      if (account) {
        account.lastAccess = Date.now();
      }
    },
  },

  actions: {
    /**
     * Initialize account switcher from localStorage
     * Called on app startup
     */
    initialize({ commit, dispatch }: any) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          const accounts = data.accounts || [];

          // Migration: move embedded cachedUserData to individual localStorage keys
          let needsRePersist = false;
          for (const account of accounts) {
            if ((account as any).cachedUserData) {
              localStorage.setItem(
                getAccountDataKey(account.email),
                (account as any).cachedUserData
              );
              delete (account as any).cachedUserData;
              needsRePersist = true;
            }
          }

          commit('setBaseEmail', data.baseEmail || '');
          commit('setAccounts', accounts);
          commit('setActiveEmail', data.activeEmail || '');
          commit('setIsMultiAccountUser', accounts.length > 0);

          if (needsRePersist) {
            dispatch('persist');
          }
        }
      } catch (error) {
        console.warn('Failed to load account switcher data:', error);
        dispatch('clear');
      }
    },

    /**
     * Save current state to localStorage
     */
    persist({ state }: any) {
      try {
        const data = {
          baseEmail: state.baseEmail,
          accounts: state.accounts,
          activeEmail: state.activeEmail,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to persist account switcher data:', error);
      }
    },

    /**
     * Clear all account switcher data
     */
    clear({ commit, state }: any) {
      // Clean up individual account data keys
      for (const account of state.accounts) {
        localStorage.removeItem(getAccountDataKey(account.email));
      }
      commit('setBaseEmail', '');
      commit('setAccounts', []);
      commit('setActiveEmail', '');
      commit('setIsMultiAccountUser', false);
      localStorage.removeItem(STORAGE_KEY);
    },

    /**
     * Register a new account after successful login
     * Called from authen.ts after PIN verification succeeds
     */
    registerAccount({ commit, dispatch, state, getters }: any, payload: {
      email: string;
      userData: any;  // The user object that gets stored in localStorage.userData
      isSSO?: boolean;
    }) {
      const { email, userData, isSSO = false } = payload;
      
      // Check if this is a plus-email
      if (!getters.isPlusEmail(email)) {
        return; // Not a multi-account user, skip
      }
      
      // Extract base email
      const baseEmail = getters.extractBaseEmail(email);
      
      // If this is first account or same base email
      if (!state.baseEmail || state.baseEmail === baseEmail) {
        commit('setBaseEmail', baseEmail);
        commit('setIsMultiAccountUser', true);

        // Generate initials and color for this company
        const initials = getters.generateInitials(userData.company_name);
        const color = getters.getColorForEmail(email);

        // Store cached user data in its own localStorage key (keeps main array lightweight)
        const encodedData = btoa(encodeURIComponent(JSON.stringify(userData)));
        localStorage.setItem(getAccountDataKey(email), encodedData);

        // Create account info (no cachedUserData embedded)
        const accountInfo: AccountInfo = {
          email: email.toLowerCase(),
          company: userData.company,
          companyName: userData.company_name,
          initials,
          color,
          lastAccess: Date.now(),
          isSSO,
        };

        commit('addAccount', accountInfo);
        commit('setActiveEmail', email.toLowerCase());

        // Persist to localStorage
        dispatch('persist');
      }
    },

    /**
     * Switch to a different account
     * This re-authenticates with Firebase and reloads the page
     */
    async switchAccount({ commit, dispatch, state, rootState }: any, email: string) {
      const account = state.accounts.find((a: AccountInfo) => a.email === email);
      
      if (!account) {
        console.error('Account not found:', email);
        return;
      }
      
      // If it's the active account, do nothing
      if (email === state.activeEmail) {
        return;
      }
      
      commit('setIsSwitching', true);
      commit('setSwitchingToCompany', account.companyName);
      
      try {
        // For SSO accounts, redirect to login flow
        if (account.isSSO) {
          // Clear current session but keep account data
          await firebase.auth().signOut();
          localStorage.removeItem('userData');
          
          // Store the target email to resume after SSO
          localStorage.setItem('switchToEmail', email);
          
          // Redirect to login
          router.replace('/login');
          return;
        }
        
        // For regular accounts, use cached credentials
        const password = rootState.authen.p || 'happily_2018';

        // Read cached user data from individual localStorage key
        let cachedUserData = localStorage.getItem(getAccountDataKey(email));

        // Fallback: data may still be on the account object (pre-migration or hot reload)
        if (!cachedUserData && (account as any).cachedUserData) {
          cachedUserData = (account as any).cachedUserData;
          if (cachedUserData) {
            localStorage.setItem(getAccountDataKey(email), cachedUserData);
          }
        }

        // Re-authenticate with Firebase first (this validates the account still exists)
        await firebase.auth().signInWithEmailAndPassword(email, password);

        // If no cached user data, fetch fresh from the server after successful auth
        if (!cachedUserData) {
          // Import dynamically to avoid circular dependency
          const { getByUser, getCompany } = await import('./cacheService');

          const emailKey = email.replace(/\./g, '||');
          let user = await getByUser(email);

          // Fallback to adviser users
          if (!user) {
            const adviserSnapshot = await firebase.database().ref(`/users_adviser/${emailKey}`).once('value');
            user = adviserSnapshot.val();
          }

          if (!user) {
            throw new Error('User not found. Please contact support.');
          }

          const company = await getCompany(user.company);

          // Build user data object (same format as authen.ts loginpincode)
          const userData = {
            company_name: (company && company.name) ? company.name : user.company,
            company: user.company,
            firstname: user.firstname,
            lastname: user.lastname,
            user_type: user.user_type,
            email: user.email,
            profilepic: user.profilepic || '',
            profilepic_crop: user.profilepic_crop || '',
            profilepic_small: user.profilepic_small || '',
            team: user.team || '',
          };

          // Encode and store for future switches
          cachedUserData = btoa(encodeURIComponent(JSON.stringify(userData)));
          localStorage.setItem(getAccountDataKey(email), cachedUserData);
        }

        // Update localStorage.userData with cached data
        localStorage.setItem('userData', cachedUserData);

        // Update version to prevent "new version" banner
        const currentVersion = localStorage.getItem('appVersion');
        if (currentVersion) {
          localStorage.setItem('userVersion', currentVersion);
        }

        // Reset the session activity timer so the user doesn't get logged out
        // after switching (the idle timer checks this on page load)
        localStorage.setItem('lastActivity', Date.now().toString());

        // Update last access
        commit('updateLastAccess', email);
        commit('setActiveEmail', email);
        dispatch('persist');
        
        // Set switching state for smooth overlay transition
        localStorage.setItem('accountSwitchInProgress', JSON.stringify({
          companyName: account.companyName,
          timestamp: Date.now()
        }));
        
        // Small delay to let overlay animate in before reload
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Reload page to pick up new company data
        // This ensures all Vuex stores get fresh data for the new company
        window.location.href = '/';
        
      } catch (error) {
        console.error('Failed to switch account:', error);
        commit('setIsSwitching', false);

        // If auth fails, the cached data might be stale
        // Remove this account and prompt re-login
        localStorage.removeItem(getAccountDataKey(email));
        commit('removeAccount', email);
        dispatch('persist');

        // Show error message
        throw new Error('Failed to switch account. Please log in again.');
      }
    },

    /**
     * Remove an account from the switcher
     */
    removeAccount({ commit, dispatch, state }: any, email: string) {
      // Clean up individual cached data key
      localStorage.removeItem(getAccountDataKey(email));
      commit('removeAccount', email);
      dispatch('persist');

      // If no accounts left, clear multi-account mode
      if (state.accounts.length === 0) {
        dispatch('clear');
      }
    },

    /**
     * Handle logout - keep account in list but clear active state
     */
    handleLogout({ commit, dispatch, state }: any) {
      // Don't clear the account list, just mark as not active
      // This way when user logs back in, they'll still see other accounts
      commit('setActiveEmail', '');
      dispatch('persist');
    },
  },

  getters: {
    /**
     * Check if an email contains a + (plus addressing)
     */
    isPlusEmail: () => (email: string): boolean => {
      if (!email) return false;
      const localPart = email.split('@')[0];
      return localPart.includes('+');
    },

    /**
     * Extract base email without the + suffix
     * e.g., tareef+acme@happily.ai -> tareef@happily.ai
     */
    extractBaseEmail: () => (email: string): string => {
      if (!email) return '';
      const [localPart, domain] = email.toLowerCase().split('@');
      const baseLocal = localPart.split('+')[0];
      return `${baseLocal}@${domain}`;
    },

    /**
     * Extract the company identifier from plus email
     * e.g., tareef+acme@happily.ai -> acme
     */
    extractCompanyTag: () => (email: string): string => {
      if (!email) return '';
      const localPart = email.split('@')[0];
      const parts = localPart.split('+');
      return parts.length > 1 ? parts[1] : '';
    },

    /**
     * Generate two-letter initials from company name
     */
    generateInitials: () => (companyName: string): string => {
      if (!companyName) return '??';
      
      const words = companyName.trim().split(/\s+/);
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return companyName.substring(0, 2).toUpperCase();
    },

    /**
     * Get a consistent color for an email (deterministic)
     */
    getColorForEmail: () => (email: string): string => {
      if (!email) return AVATAR_COLORS[0];
      
      // Simple hash of email to pick a color
      let hash = 0;
      for (let i = 0; i < email.length; i++) {
        hash = ((hash << 5) - hash) + email.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    },

    /**
     * Get accounts sorted alphabetically by company name
     */
    sortedAccounts: (state: AccountSwitcherState): AccountInfo[] => {
      return [...state.accounts].sort((a, b) => 
        a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase())
      );
    },

    /**
     * Get other accounts (not the active one)
     */
    otherAccounts: (state: AccountSwitcherState): AccountInfo[] => {
      return state.accounts.filter(a => a.email !== state.activeEmail);
    },

    /**
     * Get the active account info
     */
    activeAccount: (state: AccountSwitcherState): AccountInfo | undefined => {
      return state.accounts.find(a => a.email === state.activeEmail);
    },

    /**
     * Check if user has multiple accounts
     */
    hasMultipleAccounts: (state: AccountSwitcherState): boolean => {
      return state.accounts.length > 1;
    },

    /**
     * Check if account rail should be shown
     * Show if user is a multi-account user (has + email) AND has at least one account
     * This allows them to always add more accounts via the + button
     */
    shouldShowRail: (state: AccountSwitcherState): boolean => {
      return state.isMultiAccountUser && state.accounts.length >= 1;
    },
  },
};
